import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextChain } from '@/api/types/stowed-element'
import { shapeAffectsTextBox } from '@/lib/text/wrap-obstacles'

/** `shapeAffectsTextBox` only excludes styles that never apply an exclusion zone at all — every
 *  other check (eligibility, overlap, rotation, layering) still runs. Named here so callers ask
 *  "could this style possibly matter" without duplicating the style list. */
export function isActiveWrapStyle(style: ShapeData['wrapStyle']): boolean {
  return style === 'boundingBox' || style === 'wrap' || style === 'jump'
}

/**
 * Which chains a wrap-source shape's commit should re-split, for a shape that is NOT itself a
 * chain member.
 *
 * `handleShapeCommitted`'s `willReflowChain` (and `handleBatchShapesCommitted`'s resize cascade)
 * only ever redistribute a chain when the COMMITTED shape is itself in that chain — so moving,
 * resizing, or newly applying Text Wrap to an unrelated obstacle sitting over an existing chain
 * member never reflows anything: the frame just measures shorter next render and quietly drops
 * the displaced text on the floor instead of pushing it into the next linked frame.
 *
 * Checked against BOTH the before and after shape state, since a wrap-relevant change is often
 * exactly what moved the shape onto (or off of) a chain member in the first place — checking only
 * `afterShape` would miss "text wrap turned off" or "obstacle moved away," which also change a
 * chain member's capacity and need the same re-split.
 */
export function chainsAffectedByWrapSourceChange(
  beforeShape: ShapeData,
  afterShape: ShapeData,
  chains: readonly TextChain[],
  allShapes: readonly ShapeData[],
  zIndexOf: (id: string) => number | undefined
): { anchorUid: string; riderId: string }[] {
  if (!isActiveWrapStyle(beforeShape.wrapStyle) && !isActiveWrapStyle(afterShape.wrapStyle)) {
    return []
  }

  const targets: { anchorUid: string; riderId: string }[] = []

  for (const chain of chains) {
    for (const tb of chain.textBox) {
      const memberShape = allShapes.find((s) => s.uid != null && String(s.uid) === tb.uid)

      if (!memberShape || memberShape.id === afterShape.id) continue

      if (
        shapeAffectsTextBox(afterShape, memberShape, zIndexOf) ||
        shapeAffectsTextBox(beforeShape, memberShape, zIndexOf)
      ) {
        targets.push({ anchorUid: tb.uid, riderId: memberShape.id })
        break
      }
    }
  }

  return targets
}
